import { AlertTriangle } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";

/**
 * 「重新编排」知情确认(批⑫a 编排页重排安全网)。
 *
 * ═══ 治的是什么病 ═══════════════════════════════════════════════════════════════
 * `triggerComposeJob` 进门第一件事就是把 panelScenes / panelMotions / voiceovers /
 * panelDurations / 首帧…… 整组置 null 再 `setForm` —— 用户逐镜手改过的画面、口播、时长
 * **在点下去的那一瞬间就没了**,既没有确认也没有撤销,失败路还不回填。
 * 而两个入口(主按钮「重新编排(分镜 + 台本图)」、摘要条那颗小小的「重新编排」)都是
 * 一点即走:后者甚至只是琥珀条右端的一颗 h-7 小按钮,误点成本与「删掉整份分镜」等价。
 *
 * ★首次编排(还没有分镜)**不弹**:那时没有任何东西会被覆盖,弹窗只是噪音 ——
 *   见 VideoCreatePage `guardedCompose`,判据是 `hasStoryboardShots(form)`。
 *
 * ═══ 为什么要明写「每次结果都会有差异」 ═════════════════════════════════════════
 * 用户的心智模型常是「我只改了一个设定,重编排应该只动那一处」。实际是整份分镜重新生成,
 * 同样的设定也不会逐字复现(LLM 采样)。不说这句,用户会把「怎么全变了」读成 bug,
 * 并且在「只想微调一处」时选择重编排 —— 那是这条路上最贵的误用。
 *
 * ═══ 按钮等级(与 BareSendConfirmDialog 同一条规矩)═══════════════════════════════
 * 确认位(右下实心)= **用户已经表达的意图**(重新编排),劝返靠文案不靠抢确认位。
 * 2026-08-19 老板走查踩过反例:推荐动作坐在确认位,他点右下角实心按钮 → 走了不想走的那条路。
 */
export interface RecomposeConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 用户坚持重新编排(确认位)。 */
  onConfirm: () => void;
  /** 当前这版有几镜 —— 说清「会被覆盖的是多大一坨」,不是抽象的「你的改动」。 */
  shotCount: number;
  /** 快照槽在手 = 覆盖后还能一键切回上一版;没有则不许承诺。 */
  canRestore?: boolean;
}

export function RecomposeConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  shotCount,
  canRestore,
}: RecomposeConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>重新编排会生成全新分镜,覆盖当前这版</AlertDialogTitle>
          <AlertDialogDescription>
            AI 会按现在的设定从头编一份分镜
            {shotCount > 0 ? `,当前这 ${shotCount} 镜整份作废` : ""} ——
            <span className="font-medium text-foreground">
              你手动调过的分镜画面 / 口播文案 / 每镜时长会被一并覆盖
            </span>
            。重新编排会再扣一次编排费,以及新生成台本图的费用(按张)。
            <br />
            只想改一两镜:直接在分镜卡里改,不用重新编排;只改配音 / 字幕 / BGM /
            清晰度的,也不需要重新编排。
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/**
         * ★随机性明示:同样的设定每次编排结果都不一样。放独立警示块而不是塞进正文 ——
         *   正文已经三句话,这一句混进去必被读漏(BareSendConfirmDialog 的时长档冲突
         *   就是这么被淹掉的,老板真机踩过)。
         */}
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            同样的设定,每次编排结果都会有差异 —— 新的一版不一定比现在这版好。
            {canRestore
              ? "编排完可以在分镜区点「恢复上一版分镜」切回来。"
              : "切走这版就没有备份了。"}
          </span>
        </div>

        {/**
         * DOM 顺序 = 视觉左右(footer 是 sm:justify-between):退出在左,确认位在右。
         * 确认位放的是用户已经表达的意图(重新编排)。
         */}
        <AlertDialogFooter className="sm:justify-between">
          <AlertDialogCancel data-testid="video-recompose-cancel">先不</AlertDialogCancel>
          <AlertDialogAction data-testid="video-recompose-confirm" onClick={onConfirm}>
            重新编排
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
